import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Autoplay } from "swiper";
import { COLORS } from "../../../utils/styles/colors";
import Sanity from "../../../utils/sanity";
import { BREAKPOINTS } from "../../../utils/styles/breakpoints";

type GalleryImage = {
  url: string;
  alt?: string;
};

const GalleryWrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  text-align: center;

  ${BREAKPOINTS.MEDIUM} {
    padding: 16px 32px;
  }

  ${BREAKPOINTS.LARGE} {
    padding: 28px 32px;
  }

  ${BREAKPOINTS.XLARGE} {
    padding: 0 56px 56px 56px;
  }
`;

const Galimage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto; /* Removed fixed height */
  display: block;
  border-radius: 8px;
  object-fit: contain;
  margin: 0 auto;
`;

const StyledSwiperContainer = styled.div`
  .swiper {
    height: auto; /* Adjusted to auto to fit content */
    padding: 30px 0;
    user-select: none;
  }

  .swiper-slide {
    height: 500px; /* Set a fixed height for the slides */
    transform: scale(0.9);
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
  }

  .swiper-slide-active {
    transform: scale(1);
    opacity: 1;
    z-index: 2;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: ${COLORS.CustomSkin};
    scale: 1.2;
    user-select: none;
  }

  @media (max-width: 768px) {
    .swiper {
      height: auto;
      padding: 0;
    }

    .swiper-button-next,
    .swiper-button-prev {
      display: none;
    }

    .swiper-slide {
      height: 350px; /* Adjust slide height for smaller screens if needed */
      transform: scale(0.9);
    }
  }
`;

const GallerySection: React.FC = () => {
  const { t } = useTranslation();
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);

  useEffect(() => {
    Sanity.getGalleryImages().then((data: GalleryImage[]) => {
      if (data) {
        setGalleryImages(data);
      }
    });
  }, []);

  return (
    <GalleryWrapper>
      <h2>{t("გალერეა")}</h2>
      {galleryImages.length > 0 ? (
        <StyledSwiperContainer>
          <Swiper
            modules={[Navigation, Autoplay]}
            spaceBetween={1}
            slidesPerView={3}
            centeredSlides={true}
            loop={true}
            navigation
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            className="gallery-swiper"
            breakpoints={{
              0: {
                slidesPerView: 1, // 👈 on small screens (mobile)
              },
              768: {
                slidesPerView: 3, // 👈 on tablets and up
              },
            }}
          >
            {galleryImages.map((image, index) => (
              <SwiperSlide
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {" "}
                {/* Inline styles for centering */}
                <Galimage
                  src={image.url}
                  alt={image.alt || `Gallery Image ${index + 1}`}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </StyledSwiperContainer>
      ) : (
        <p>{t("Loading gallery images...")}</p>
      )}
    </GalleryWrapper>
  );
};

export default GallerySection;
